import { useState } from "react";
import { Canvas } from "../scrapbook/Canvas";
import { ToolBar } from "../scrapbook/ToolBar";
import { Button } from "@/components/ui/button";
import { X, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import type { Canvas as FabricCanvas } from "fabric/fabric-impl";

interface EditableImageProps {
  slug: string;
  screenshotUrl: string;
  fabricJson?: any;
  onSave?: (canvas: FabricCanvas) => Promise<void>;
  onCancel?: () => void;
  onReset?: () => Promise<void>;
  className?: string;
  isEditMode?: boolean;
}

export const EditableImage = ({
  slug,
  screenshotUrl,
  fabricJson,
  onSave,
  onCancel,
  onReset,
  className = "h-[450px]",
  isEditMode = true
}: EditableImageProps) => {
  const [activeTool, setActiveTool] = useState<"select" | "draw" | "rectangle" | "move" | "text" | "crop">("select");
  const [fabricCanvas, setFabricCanvas] = useState<FabricCanvas | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const handleToolChange = (tool: "select" | "draw" | "rectangle" | "move" | "text" | "crop") => {
    setActiveTool(tool);
  };

  const handleZoom = (direction: "in" | "out") => {
    if (!fabricCanvas) return;

    const delta = direction === "in" ? 0.1 : -0.1;
    const newZoom = Math.min(Math.max(zoomLevel + delta, 0.1), 5);

    // Get the center point of the viewport
    const center = {
      x: fabricCanvas.width! / 2,
      y: fabricCanvas.height! / 2
    };

    // Apply zoom from the center point
    fabricCanvas.zoomToPoint({ x: center.x, y: center.y }, newZoom);
    setZoomLevel(newZoom);
    fabricCanvas.renderAll();
  };

  const handleReset = () => {
    if (onReset) {
      onReset().catch((error) => {
        console.error('Error resetting canvas:', error);
        toast.error('Failed to reset image');
      });
    }
  };

  const handleSave = async () => {
    if (fabricCanvas && onSave) {
      await onSave(fabricCanvas);
      toast.success('Changes saved successfully');
    }
  };

  return (
    <div className={`relative flex flex-col ${className}`}>
      {isEditMode && (
        <div className="flex-shrink-0 mb-4">
          <ToolBar
            activeTool={activeTool}
            onToolChange={handleToolChange}
            onZoom={handleZoom}
            onReset={handleReset}
          />
        </div>
      )}
      <div className="border border-gray-200 rounded-lg overflow-hidden flex-1 min-h-0">
        <Canvas
          companySlug={slug}
          screenshotUrl={screenshotUrl}
          activeTool={activeTool}
          onCanvasInitialized={setFabricCanvas}
          initialFabricJson={fabricJson}
          isEditMode={isEditMode}
        />
      </div>
      {isEditMode && (
        <div className="flex justify-end gap-2 mt-4 flex-shrink-0">
          <Button
            variant="outline"
            onClick={onCancel}
            className="gap-1"
          >
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            className="gap-1"
          >
            <Save className="h-4 w-4" />
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};